import type { CmtsAdminDependencyImpact } from "./cmtsAdminTypes.js";

type Row = Record<string, unknown>;

const activeStates = new Set(["active", "open", "assigned", "in progress", "required", "submitted", "under review", "pending", "scheduled"]);

function text(row: Row): string {
  return Object.values(row).map((value) => String(value ?? "")).join(" ").toLowerCase();
}

export function buildCmtsAdminDependencyImpact(target: Row, data: Record<string, readonly Row[]> = {}): CmtsAdminDependencyImpact {
  const code = String(target.code ?? target.id ?? target.label ?? "").toLowerCase();
  const label = String(target.label ?? target.name ?? target.code ?? target.id ?? "admin record");
  const impactedEntities = Object.entries(data).flatMap(([entityType, rows]) => rows
    .filter((row) => code && text(row).includes(code))
    .slice(0, 25)
    .map((row) => ({
      entityType,
      entityId: String(row.id ?? row.complaintId ?? row.taskId ?? row.notificationId ?? row.code ?? label),
      entityLabel: String(row.title ?? row.name ?? row.label ?? row.complaintNumber ?? row.code ?? entityType),
      impactType: activeStates.has(String(row.status ?? row.active ?? "").toLowerCase()) ? "ACTIVE_USAGE" : "REFERENCE_USAGE",
      remediation: "Provide a replacement mapping or resolve active usage before deactivation.",
    })));
  const blockingCount = impactedEntities.filter((item) => item.impactType === "ACTIVE_USAGE").length;
  const warningCount = impactedEntities.length - blockingCount;
  return { canProceed: blockingCount === 0, severity: blockingCount > 0 ? "BLOCKING" : warningCount > 0 ? "WARNING" : "INFO", blockingCount, warningCount, impactedEntities, replacementRequired: blockingCount > 0 };
}

export function validateCmtsAdminUniqueCode(rows: readonly Row[], candidate: Row, currentId?: string): string | null {
  const code = String(candidate.code ?? "").trim().toLowerCase();
  if (!code) return "Code is required.";
  const group = String(candidate.group ?? candidate.masterType ?? candidate.level ?? candidate.ruleType ?? "").trim().toLowerCase();
  const tenantId = String(candidate.tenantId ?? "").trim().toLowerCase();
  const duplicate = rows.find((row) => String(row.id ?? "") !== String(currentId ?? "") && String(row.code ?? "").trim().toLowerCase() === code && String(row.tenantId ?? "").trim().toLowerCase() === tenantId && String(row.group ?? row.masterType ?? row.level ?? row.ruleType ?? "").trim().toLowerCase() === group);
  return duplicate ? `Duplicate code ${String(candidate.code)} exists in this tenant and group.` : null;
}
